import {useEffect} from 'react'
import {useTranslation} from 'react-i18next'
import Header from '@/components/Header'
import Footer from '@/components/Footer'
import LanguagePageView from '@/components/language-page/LanguagePageView'
import LanguagePageMissing from '@/components/language-page/LanguagePageMissing'
import {Button} from '@/components/ui/button'
import {Helmet} from 'react-helmet-async'
import {LANGUAGE_PAGES} from '@/lib/languagePages'
import {useParams} from 'react-router-dom'
import {useLanguagePage} from '@/hooks/useLanguagePage'
import {isSupportedLocale} from '@/lib/locales'
import type {LanguagePageVariant} from '@/types/languagePage'

interface Props {
  variant: LanguagePageVariant
}

const LoadingShell = () => (
  <div className="min-h-screen bg-background">
    <Header />
    <main className="container mx-auto px-4 py-32 text-center">
      <div className="h-8 w-48 bg-secondary rounded mx-auto animate-pulse" />
    </main>
    <Footer />
  </div>
)

const ErrorShell = ({onRetry}: {onRetry: () => void}) => (
  <div className="min-h-screen bg-background">
    <Header />
    <main className="container mx-auto px-4 py-32 max-w-md text-center">
      <h1 className="text-2xl font-display font-semibold text-foreground mb-3">
        Something went wrong
      </h1>
      <p className="text-muted-foreground mb-6">
        We couldn't load this page. Please try again in a moment.
      </p>
      <Button onClick={onRetry}>Try again</Button>
    </main>
    <Footer />
  </div>
)

const LanguagePageContainer = ({variant}: Props) => {
  const {language, locale} = useParams<{language: string; locale?: string}>()
  const {data: page, isLoading, isError, refetch} = useLanguagePage(variant, language, locale)
  const {i18n} = useTranslation()

  // The URL declares this page's language, so keep the surrounding chrome
  // (Header/Footer) in sync — e.g. landing directly on /fr/find-a-tutor/spanish
  // should show a French chrome, not just a French body.
  useEffect(() => {
    const desired = locale && isSupportedLocale(locale) ? locale : 'en'
    if (i18n.language !== desired) i18n.changeLanguage(desired)
  }, [locale, i18n])

  if (isLoading) return <LoadingShell />
  if (isError) return <ErrorShell onRetry={() => refetch()} />
  if (!page) {
    return (
      <LanguagePageMissing variant={variant} slug={(language ?? '').toLowerCase()} locale={locale} />
    )
  }

  // Mirrors LanguagePagesController's fallback so the title the browser sets
  // after hydration is the one the server sent. Title (and robots) only: the
  // Rails layout owns description/og/twitter (#674).
  const languageName = LANGUAGE_PAGES.find((entry) => entry.slug === page.slug)?.name ?? page.language
  const defaultLearnTitle = `Learn ${languageName} | Online Language Lessons | Jynio`
  const metaTitle = page.seoTitle ?? (variant === 'learn' ? defaultLearnTitle : page.title)

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>{metaTitle}</title>
        {page.noIndex ? <meta name="robots" content="noindex" /> : null}
      </Helmet>
      <Header />
      <LanguagePageView page={page} />
      <Footer />
    </div>
  )
}

export default LanguagePageContainer
